import {
  LayoutNode,
  ListLayout,
  ListLayoutOptions,
} from 'react-stately/useVirtualizerState';
import React, { Key } from 'react';
import {
  InvalidationContext,
  Rect,
  Size,
} from 'react-stately/useVirtualizerState';
import { Node } from '@react-types/shared';

/**
 * Extends `ListLayout` and allows items of the list to have width based on the content.
 * `ListLayout` by default sets width based on the virtualizer's visible rect. This means the
 * overflow of list items will be clipped. In `VariableWidthListLayout`, the width of the list
 * depends on the content of the visible items.
 *
 * NOTE: At the time of writing this class, Virtualization in @react-aria and @react-stately don't
 * have proper documentation, and it wasn't clear if there could be better ways of letting list's
 * width grow based on the content.
 */
export class VariableWidthListLayout<T> extends ListLayout<T> {
  /**
   * content width of items are stored here, if the content width is bigger than the Virtualizer's
   * visible rect's width.
   */
  keyToWidth = new Map<Key, number>();
  private visibleContentWidth: number = 0;

  constructor(options: ListLayoutOptions) {
    super(options);
  }

  override buildItem(node: Node<T>, x: number, y: number): LayoutNode {
    const previousLayoutNode = this.layoutNodes.get(node.key);
    const layoutNode = super.buildItem(node, x, y);
    if (this.visibleContentWidth) {
      layoutNode.layoutInfo.rect.width = this.visibleContentWidth;
    }
    // If only the width changed (not the content) and the item was already
    // measured, don't mark as estimated. This prevents re-measurement cascades
    // where width expansion → re-measure → new wider items → more width
    // expansion can exceed React's update depth limit.
    if (
      previousLayoutNode &&
      previousLayoutNode.node === node &&
      !previousLayoutNode.layoutInfo.estimatedSize
    ) {
      layoutNode.layoutInfo.estimatedSize = false;
    }
    return layoutNode;
  }

  /**
   * Allows for overriding buildCollection in a sub-class
   */
  protected doBuildCollection(): LayoutNode[] {
    return super.buildCollection();
  }

  buildCollection(): LayoutNode[] {
    this.visibleContentWidth = this.getVisibleContentWidth();
    const layoutNodes = this.doBuildCollection();
    this.contentSize.width = this.visibleContentWidth;
    return layoutNodes;
  }

  override shouldInvalidate(newRect: Rect, oldRect: Rect): boolean {
    return (
      super.shouldInvalidate(newRect, oldRect) ||
      this.getVisibleContentWidth() !== this.visibleContentWidth
    );
  }

  override shouldInvalidateEverything(
    invalidationContext: InvalidationContext<ListLayoutOptions>
  ): boolean {
    return (
      super.shouldInvalidateEverything(invalidationContext) ||
      // in buildChild, if invalidateEverything is false and y is not changed, it will reuse the existing layoutInfo.
      // which can be problematic, if it was created in a time when the visible content width was different.
      // A more efficient approach (instead of rebuilding the whole collection), might be to set layout width to
      // visibleContentWidth, in getVisibleLayoutInfos, if mutation is ok.
      // UPDATE: using getFinalLayoutInfo seems to be a legitimate last minute way to mutate layout infos.
      this.contentSize?.width !== this.visibleContentWidth
    );
  }

  override updateItemSize(key: React.Key, size: Size): boolean {
    const changed = super.updateItemSize(key, size);
    const layoutNode = this.layoutNodes.get(key);
    // If no layoutInfo, item has been deleted/removed.
    if (!layoutNode) return false;
    const layoutInfo = layoutNode.layoutInfo;
    if (layoutInfo && size.width > this.contentSize.width) {
      this.keyToWidth.set(key, size.width);
      // Update contentSize.width immediately so that subsequent items measured
      // in the same render cycle compare against the latest max width, avoiding
      // redundant invalidations that can exceed React's update depth limit.
      this.contentSize.width = size.width;
      return true;
    } else {
      return changed;
    }
  }

  private getVisibleContentWidth() {
    return this.virtualizer
      ? Math.max(
          this.virtualizer.visibleRect.width,
          ...Array.from(this.keyToWidth.values())
        )
      : 0;
  }
}
